import {
  TextField,
  Typography,
} from '@forethought-technologies/forethought-elements';
import { Box, styled } from '@mui/material';
import {
  sendConversationFeedback,
  sendPartialCsatFeedback,
  submitCsatV3,
} from 'actions/workflows-v2/workflowsV2';
import { useContinueExecutionAfterCsat } from 'components/App/hooks/useContinueExecutionAfterCsat';
import OptionButton from 'components/OptionButton';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useEffect, useMemo, useRef } from 'react';
import { selectCsatConfigRevamped } from 'reducers/config';
import { selectIsMobile } from 'reducers/main';
import {
  selectCsatStates,
  selectFeatureFlags,
  selectIsSolveConfigPreview,
  setCsatStatesByKey,
  goToStage,
  selectLoading,
  setLatestStepRerouted,
} from 'reducers/workflows';
import { debounce } from 'utils/debounce';
import {
  ADDITIONAL_FEEDBACK_STAGE,
  csatOneToFiveOptions,
  HIGH_RATING_REASONS_STAGE,
  LOW_RATING_CAP,
  LOW_RATING_REASONS_STAGE,
  RESOLUTION_CONFIRMATION_STAGE,
  SUBMIT_STAGE,
} from './constants';
import { sendTrackingEvent } from 'actions/workflows/workflows';
import { CsatCloseButton } from './CsatCloseButton';
import { OrgThemedButton } from 'components/OrgThemedButton';

export const CsatSurveyContent = () => {
  const dispatch = useAppDispatch();
  const endOfCsatRef = useRef<HTMLSpanElement>(null);
  const isSolveConfigPreview = useAppSelector(selectIsSolveConfigPreview);
  const csatConfig = useAppSelector(selectCsatConfigRevamped);
  const csatStates = useAppSelector(selectCsatStates);
  const isMobile = useAppSelector(selectIsMobile);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const loading = useAppSelector(selectLoading);
  const continueExecutionAfterCsatDismiss = useContinueExecutionAfterCsat();
  const isCsatConfigRevampEnabled = featureFlags.includes('csat_config_revamp');
  const isNegativeRatingRoutingEnabled = featureFlags.includes(
    'csat_negative_rating_routing',
  );
  const isSubmitDisabled = isNegativeRatingRoutingEnabled && loading;
  const { triggeredFrom } = useAppSelector(selectCsatStates);

  const {
    actionToExecuteAfterCsatDismiss,
    additionalFeedback,
    currentStage,
    highRatingReasonIndices,
    isCsatVisible,
    isEditing,
    isResolved,
    lowRatingReasonIndices,
    rating: selectedRating,
  } = csatStates;

  useEffect(() => {
    if (endOfCsatRef.current) {
      endOfCsatRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentStage]);

  useEffect(() => {
    if (csatConfig && isCsatConfigRevampEnabled && isCsatVisible) {
      dispatch(
        sendTrackingEvent({
          event_type: 'widget-csat-opened',
          triggered_from: triggeredFrom,
        }),
      );
    }
  }, [
    csatConfig,
    isCsatConfigRevampEnabled,
    isCsatVisible,
    triggeredFrom,
    dispatch,
  ]);

  const handleDebouncedSubmitCsat = useMemo(
    () =>
      debounce((sendFeedbackToHelpdesk = false) => {
        if (!isSolveConfigPreview) {
          dispatch(submitCsatV3({ sendFeedbackToHelpdesk }));
        }
      }, 300),
    [dispatch, isSolveConfigPreview],
  );

  const handleDebouncedSubmitCsatAndFeedback = useMemo(
    () =>
      debounce(() => {
        if (!isSolveConfigPreview) {
          dispatch(submitCsatV3({}));
          dispatch(sendPartialCsatFeedback());
        }
      }, 300),
    [dispatch, isSolveConfigPreview],
  );

  if (!csatConfig || !isCsatConfigRevampEnabled || !isCsatVisible) {
    return null;
  }

  const {
    negative_rating_routing_intent: globalNegativeRatingRoutingIntent,
    no_resolve_option: noResolveOption,
    one_to_five_labels: oneToFiveLabels,
    question_for_additional_feedback: questionForAdditionalFeedback,
    question_for_high_ratings: questionForHighRatings,
    question_for_low_ratings: questionForLowRatings,
    question_for_resolution_confirmation: questionForResolutionConfirmation,
    rating_question: ratingQuestion,
    reasons_for_high_ratings: reasonsForHighRatings,
    reasons_for_low_ratings: reasonsForLowRatings,
    should_request_additional_feedback: shouldRequestAdditionalFeedback,
    should_request_feedback_for_high_ratings:
      shouldRequestFeedbackForHighRatings,
    should_request_feedback_for_low_ratings: shouldRequestFeedbackForLowRatings,
    should_request_resolution_confirmation: shouldRequestResolutionConfirmation,
    should_use_rating_based_submit_message: shouldUseRatingBasedSubmitMessage,
    style,
    yes_resolve_option: yesResolveOption,
  } = csatConfig;

  const getCsatFeedback = () => {
    if (selectedRating <= LOW_RATING_CAP) {
      return lowRatingReasonIndices.map(
        index => reasonsForLowRatings?.[index] || '',
      );
    } else {
      return highRatingReasonIndices.map(
        index => reasonsForHighRatings?.[index] || '',
      );
    }
  };

  const chosenStyleOption = csatOneToFiveOptions.find(
    option => option.value === style,
  );

  const handleSubmit = async () => {
    // submit the CSAT
    handleDebouncedSubmitCsat(true);

    dispatch(
      sendConversationFeedback({
        feedbackFields: {
          additionalFeedback: additionalFeedback,
          csat_feedback: getCsatFeedback(),
          csat_resolve: isResolved,
          csat_score: selectedRating,
          csat_trigger_point_prompted: true, // if CSAT has been submitted already, don't trigger future trigger point step
          is_partial_submission: false,
          resume_from_trigger_point:
            actionToExecuteAfterCsatDismiss === 'resume-from-trigger-point',
          routing_intent_workflow_id: isNegativeRatingRoutingEnabled
            ? globalNegativeRatingRoutingIntent
            : undefined,
        },
      }),
    );

    if (
      isNegativeRatingRoutingEnabled &&
      selectedRating <= LOW_RATING_CAP &&
      globalNegativeRatingRoutingIntent
    ) {
      dispatch(setLatestStepRerouted());
    }

    dispatch(
      setCsatStatesByKey({
        submittedRating: selectedRating,
      }),
    );

    dismissDrawerAfterSubmit();
  };

  const dismissDrawerAfterSubmit = () => {
    // we should not dismiss the confirmation if rating based message is enabled
    if (shouldUseRatingBasedSubmitMessage) {
      return;
    }
    setTimeout(() => {
      // don't resume from trigger point because "submit" already did that
      if (actionToExecuteAfterCsatDismiss !== 'resume-from-trigger-point') {
        continueExecutionAfterCsatDismiss();
      }

      dispatch(
        setCsatStatesByKey({
          isCsatVisible: false,
        }),
      );
    }, 1000);
  };

  return (
    <Box>
      <Box display='flex' flex={1} flexDirection='column' gap='32px'>
        <Section>
          <Box
            dir='auto'
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '8px',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='font14Bold'>{ratingQuestion}</Typography>
            <Box sx={{ alignSelf: 'flex-start' }}>
              <CsatCloseButton />
            </Box>
          </Box>
          <Box display='flex' gap='12px' justifyContent='center'>
            {chosenStyleOption &&
              chosenStyleOption.icons.map((icon, index) => {
                const rating = index + 1;
                return (
                  <RatingButton
                    key={index}
                    onClick={() => {
                      dispatch(setCsatStatesByKey({ rating }));
                      handleDebouncedSubmitCsatAndFeedback();
                      // if rating is different, clear some of the states
                      const isOldRatingLow = selectedRating <= LOW_RATING_CAP;
                      const isNewRatingLow = rating <= LOW_RATING_CAP;
                      if (isOldRatingLow !== isNewRatingLow) {
                        dispatch(
                          setCsatStatesByKey({
                            highRatingReasonIndices: [],
                            lowRatingReasonIndices: [],
                          }),
                        );
                      }
                      dispatch(
                        goToStage(
                          isNewRatingLow
                            ? LOW_RATING_REASONS_STAGE
                            : HIGH_RATING_REASONS_STAGE,
                        ),
                      );
                    }}
                    selected={
                      chosenStyleOption.value === 'star'
                        ? rating <= selectedRating
                        : selectedRating < 0
                          ? true
                          : rating === selectedRating
                    }
                    type='button'
                  >
                    <StyleCardImg
                      alt={chosenStyleOption.iconNames[index]}
                      src={
                        chosenStyleOption.value === 'star' &&
                        rating <= selectedRating
                          ? chosenStyleOption.selectedIcon
                          : icon
                      }
                    />
                    <Typography variant='font12'>
                      {oneToFiveLabels[index]}
                    </Typography>
                  </RatingButton>
                );
              })}
          </Box>
        </Section>

        {shouldRequestFeedbackForHighRatings &&
          selectedRating > LOW_RATING_CAP &&
          currentStage >= HIGH_RATING_REASONS_STAGE && (
            <Section>
              <Box dir='auto' textAlign='center'>
                <Typography variant='font14'>
                  {questionForHighRatings}
                </Typography>
              </Box>
              <OptionsContainer>
                {reasonsForHighRatings.map((reason, index) => (
                  <OptionButton
                    isMobile={isMobile}
                    isSelected={highRatingReasonIndices.includes(index)}
                    key={reason}
                    onClick={() => {
                      const isSelected =
                        highRatingReasonIndices.includes(index);
                      const updatedIndices = isSelected
                        ? highRatingReasonIndices.filter(
                            reasonIndex => reasonIndex !== index,
                          )
                        : [...highRatingReasonIndices, index];

                      dispatch(
                        setCsatStatesByKey({
                          highRatingReasonIndices: updatedIndices,
                        }),
                      );
                      dispatch(goToStage(RESOLUTION_CONFIRMATION_STAGE));
                      handleDebouncedSubmitCsatAndFeedback();
                    }}
                    text={reason}
                  />
                ))}
              </OptionsContainer>
            </Section>
          )}

        {shouldRequestFeedbackForLowRatings &&
          selectedRating > 0 &&
          selectedRating <= LOW_RATING_CAP &&
          currentStage >= LOW_RATING_REASONS_STAGE && (
            <Section>
              <Box dir='auto' textAlign='center'>
                <Typography variant='font14'>
                  {questionForLowRatings}
                </Typography>
              </Box>
              <OptionsContainer>
                {reasonsForLowRatings.map((reason, index) => (
                  <OptionButton
                    isMobile={isMobile}
                    isSelected={lowRatingReasonIndices.includes(index)}
                    key={reason}
                    onClick={() => {
                      const isSelected = lowRatingReasonIndices.includes(index);
                      const updatedIndices = isSelected
                        ? lowRatingReasonIndices.filter(
                            reasonIndex => reasonIndex !== index,
                          )
                        : [...lowRatingReasonIndices, index];

                      dispatch(
                        setCsatStatesByKey({
                          lowRatingReasonIndices: updatedIndices,
                        }),
                      );
                      dispatch(goToStage(RESOLUTION_CONFIRMATION_STAGE));
                      handleDebouncedSubmitCsatAndFeedback();
                    }}
                    text={reason}
                  />
                ))}
              </OptionsContainer>
            </Section>
          )}

        {shouldRequestResolutionConfirmation &&
          currentStage >= RESOLUTION_CONFIRMATION_STAGE && (
            <Section>
              <Box dir='auto' textAlign='center'>
                <Typography variant='font14'>
                  {questionForResolutionConfirmation}
                </Typography>
              </Box>
              <OptionsContainer>
                <OptionButton
                  isMobile={isMobile}
                  isSelected={isResolved}
                  onClick={() => {
                    dispatch(
                      setCsatStatesByKey({
                        isResolved: true,
                      }),
                    );
                    dispatch(goToStage(ADDITIONAL_FEEDBACK_STAGE));
                    handleDebouncedSubmitCsatAndFeedback();
                  }}
                  text={yesResolveOption}
                />
                <OptionButton
                  isMobile={isMobile}
                  isSelected={isResolved === false}
                  onClick={() => {
                    dispatch(
                      setCsatStatesByKey({
                        isResolved: false,
                      }),
                    );
                    dispatch(goToStage(ADDITIONAL_FEEDBACK_STAGE));
                    handleDebouncedSubmitCsatAndFeedback();
                  }}
                  text={noResolveOption}
                />
              </OptionsContainer>
            </Section>
          )}

        {shouldRequestAdditionalFeedback &&
          currentStage >= ADDITIONAL_FEEDBACK_STAGE && (
            <Section>
              <Box dir='auto' textAlign='center'>
                <Typography variant='font14'>
                  {questionForAdditionalFeedback}
                </Typography>
              </Box>
              <TextField
                aria-label='additional feedback'
                dir='auto'
                multiline={true}
                onChange={e => {
                  dispatch(
                    setCsatStatesByKey({
                      additionalFeedback: e.target.value,
                    }),
                  );
                  dispatch(goToStage(SUBMIT_STAGE));
                  handleDebouncedSubmitCsatAndFeedback();
                }}
                rows={3}
                value={additionalFeedback}
              />
            </Section>
          )}

        {currentStage >= ADDITIONAL_FEEDBACK_STAGE && (
          <OrgThemedButton
            disabled={isSubmitDisabled}
            fullWidth
            onClick={() => {
              dispatch(
                setCsatStatesByKey({
                  hasSubmitted: true,
                  isEditing: false,
                }),
              );
              dispatch(goToStage(SUBMIT_STAGE));

              handleSubmit();
            }}
            variant='contained'
          >
            {isEditing ? 'Update' : 'Submit'}
          </OrgThemedButton>
        )}
      </Box>
      <span ref={endOfCsatRef} />
    </Box>
  );
};

export const RatingButton = styled('button')<{ selected: boolean }>`
  outline: none;
  display: flex;
  flex-direction: column;
  flex-basis: 20%;
  gap: 4px;
  padding-inline: 0;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  ${props => !props.selected && 'opacity: 0.4;'}
  transition: transform 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    opacity: 1;
    transform: scale(1.1);
  }
`;

export const StyleCardImg = styled('img')`
  height: 40px;
  width: 40px;
`;

const Section = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OptionsContainer = styled('div')`
  display: flex;
  gap: 4px;
  justify-content: center;
  width: 100%;
  flex-wrap: wrap;
`;
